import { reactive } from 'vue';
import { $api } from '@/main';
import router from '@/router';

interface User {
  id: number;
  pseudo: string;
  email: string;
  numero_telephone: string;
  adresse: string;
  ville: string;
  role: string;
}

interface AdminAuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const state = reactive<AdminAuthState>({
  user: localStorage.getItem('admin_user') ? JSON.parse(localStorage.getItem('admin_user')!) : null,
  token: localStorage.getItem('admin_token'),
  isAuthenticated: !!localStorage.getItem('admin_token'),
  loading: false,
  error: null
});

export const useAdminAuthStore = () => {
  const login = async (identifier: string, password: string) => {
    state.loading = true;
    state.error = null;

    try {
      const response = await $api.post('/api/login', {
        identifier,
        password
      });

      const data = response.data;

      // Vérification email requise
      if (data.requires_verification) {
        console.log('🔐 Vérification requise pour user_id:', data.user_id);
        return { 
          success: false, 
          requiresVerification: true,
          userId: data.user_id,
          message: data.message || 'Un code de vérification a été envoyé.'
        };
      }

      const { user, token } = data;

      // Vérifier si l'utilisateur est admin ou super_admin
      if (user.role !== 'admin' && user.role !== 'super_admin') {
        return { 
          success: false, 
          error: 'Accès non autorisé. Compte administrateur requis.' 
        };
      }

      // Stocker le token et l'utilisateur
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_user', JSON.stringify(user));
      
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;

      $api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, user, role: user.role };
    } catch (error: any) {
      console.error('Erreur login admin:', error);
      
      // Gérer le cas où la réponse contient requires_verification
      if (error.response?.data?.requires_verification) {
        console.log('🔐 Vérification requise (catch):', error.response.data);
        return { 
          success: false, 
          requiresVerification: true,
          userId: error.response.data.user_id,
          message: error.response.data.message || 'Un code de vérification a été envoyé.'
        };
      }
      
      state.error = error.response?.data?.message || error.message || 'Erreur de connexion';
      return { success: false, error: state.error };
    } finally {
      state.loading = false;
    }
  };

  const logout = async () => {
    try {
      if (state.token) {
        await $api.post('/api/logout');
      }
    } catch (error) {
      console.error('Erreur logout:', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      delete $api.defaults.headers.common['Authorization'];
      
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      
      router.push('/admin/login');
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      state.isAuthenticated = false;
      return false;
    }

    try {
      $api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await $api.get('/api/user');
      
      const user = response.data;
      
      if (user.role !== 'admin' && user.role !== 'super_admin') {
        await logout();
        return false;
      }

      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      localStorage.setItem('admin_user', JSON.stringify(user));
      
      return true;
    } catch (error) {
      console.error('Erreur vérification auth admin:', error);
      await logout();
      return false;
    }
  };

  return {
    state,
    login,
    logout,
    checkAuth
  };
};